import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Play } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { saveToGlobalStore } from '../utils/globalAudioStore';
import { pushStoryStateToServer } from '../utils/storyState';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// 8 domains → each mapped to a REAL celestial body with signature character.
// type drives unique visual traits (Saturn rings, Jupiter bands, Mars craters, etc.)
const DOMAINS = [
  // Vedic karaka (significator) mapping — each domain is ruled by its classical graha:
  //   Sun (Surya)     → Aarogya (health, vitality, life-force)
  //   Venus (Shukra)  → Kalatra (spouse, love, marriage)
  //   Saturn (Shani)  → Karma (discipline, profession, longevity of career)
  //   Jupiter (Guru)  → Dhana (wealth, prosperity, abundance)
  //   Moon (Chandra)  → Matru (mother, home, family, emotions)
  //   Mercury (Budh)  → Vidya (intellect, learning, communication)
  //   Ketu            → Moksha (liberation, spirituality, detachment)
  //   Mars (Mangal)   → Bhumi / Parakrama (land, adventure, long journeys)
  { id:1, name:'Health',    key:'health',    type:'sun',     lord:'Surya',     color:'#FFD24A', grad:['#FFFBD0','#FFD24A','#E89020','#8A4A08'], startDeg:15,  speed:1.00 },
  { id:2, name:'Marriage',  key:'marriage',  type:'venus',   lord:'Shukra',    color:'#FFCE96', grad:['#FFE9C2','#FFCE96','#C88A4A','#6B3E1A'], startDeg:60,  speed:0.86 },
  { id:3, name:'Career',    key:'career',    type:'saturn',  lord:'Shani',     color:'#E8C278', grad:['#FFEBC0','#E8C278','#9E7830','#523A10'], startDeg:105, speed:0.72 },
  { id:4, name:'Wealth',    key:'wealth',    type:'jupiter', lord:'Guru',      color:'#E8A36E', grad:['#F8D8B0','#E8A36E','#A86230','#5A2E10'], startDeg:150, speed:1.12 },
  { id:5, name:'Family',    key:'family',    type:'moon',    lord:'Chandra',   color:'#DCD4F5', grad:['#F0EAFF','#DCD4F5','#8A80B8','#3A3060'], startDeg:195, speed:0.95 },
  { id:6, name:'Education', key:'education', type:'mercury', lord:'Budh',      color:'#8BC9B2', grad:['#C4E8D8','#8BC9B2','#4A7E6A','#1E3E32'], startDeg:240, speed:1.22 },
  { id:7, name:'Spiritual', key:'spiritual', type:'ketu',    lord:'Ketu',      color:'#9E8AC7', grad:['#D9CFEA','#9E8AC7','#5A4A82','#2A1F4A'], startDeg:285, speed:0.78 },
  { id:8, name:'Travel',    key:'travel',    type:'mars',    lord:'Mangal',    color:'#E85C3A', grad:['#FFBAA0','#E85C3A','#9E2818','#4A0E08'], startDeg:330, speed:1.06 },
];

const PHRASES = [
  'Aligning the celestial bodies...',
  'Reading your planetary positions...',
  'The stars whisper your story...',
  'Mapping karma across lifetimes...',
  'Decoding planetary influences...',
  'Channeling ancient Vedic wisdom...',
  'Your cosmic blueprint is forming...',
];

const STATE_KEY = 'past_reels_gen_state';

// -------- Planet body: characterful CSS render per type --------
// Each type = a real celestial body with a distinctive silhouette.
export const PlanetBody = ({ d, size, active }) => {
  const sphere = (
    <div style={{
      width: size, height: size, borderRadius: '50%', position: 'relative', overflow: 'hidden',
      boxShadow: `inset -${size * 0.12}px -${size * 0.12}px ${size * 0.32}px rgba(0,0,0,0.55), inset ${size * 0.05}px ${size * 0.05}px ${size * 0.14}px rgba(255,255,255,0.25)`,
      background: `radial-gradient(circle at 32% 28%, ${d.grad[0]} 0%, ${d.grad[1]} 30%, ${d.grad[2]} 65%, ${d.grad[3]} 94%)`,
    }}>
      {/* Type-specific surface overlays */}
      {d.type === 'earth' && (
        <>
          {/* continents — soft green/brown blobs */}
          <div style={{ position: 'absolute', width: '35%', height: '22%', top: '28%', left: '18%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, #4EA86A 0%, #2E7A48 60%, transparent 85%)', filter: 'blur(0.6px)', opacity: 0.85 }} />
          <div style={{ position: 'absolute', width: '28%', height: '18%', top: '55%', left: '50%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, #4EA86A 0%, #2E7A48 70%, transparent 92%)', filter: 'blur(0.6px)', opacity: 0.8 }} />
          <div style={{ position: 'absolute', width: '20%', height: '14%', top: '38%', left: '62%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, #4EA86A 0%, #2E7A48 70%, transparent 90%)', filter: 'blur(0.5px)', opacity: 0.7 }} />
          {/* cloud wisps */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(ellipse at 60% 30%, rgba(255,255,255,0.32) 0%, transparent 22%), radial-gradient(ellipse at 25% 70%, rgba(255,255,255,0.2) 0%, transparent 18%)' }} />
        </>
      )}
      {d.type === 'venus' && (
        <>
          {/* thick hazy cream clouds */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'repeating-radial-gradient(ellipse at 40% 35%, rgba(255,240,200,0.3) 0%, rgba(255,220,160,0.15) 8%, transparent 16%)' }} />
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(ellipse at 70% 55%, rgba(255,230,180,0.4) 0%, transparent 35%)' }} />
        </>
      )}
      {d.type === 'sun' && (
        <>
          {/* corona glow */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(circle at 50% 50%, rgba(255,245,180,0.7) 0%, rgba(255,200,60,0.35) 40%, transparent 70%)',
            mixBlendMode: 'screen' }} />
          {/* surface granulation */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'repeating-radial-gradient(circle at 40% 40%, rgba(255,140,20,0.25) 0%, transparent 6%)' }} />
        </>
      )}
      {d.type === 'jupiter' && (
        <>
          {/* horizontal band stripes */}
          {[0.22, 0.38, 0.52, 0.68, 0.82].map((pos, i) => (
            <div key={i} style={{
              position: 'absolute', left: 0, right: 0,
              top: `${pos * 100}%`, height: '7%',
              background: i % 2 === 0
                ? 'linear-gradient(90deg, transparent, rgba(140,70,30,0.5) 15%, rgba(180,100,50,0.55) 50%, rgba(140,70,30,0.5) 85%, transparent)'
                : 'linear-gradient(90deg, transparent, rgba(255,210,160,0.35) 15%, rgba(255,220,180,0.4) 50%, rgba(255,210,160,0.35) 85%, transparent)',
              borderRadius: '50%', filter: 'blur(0.4px)',
            }} />
          ))}
          {/* great red spot */}
          <div style={{ position: 'absolute', width: '22%', height: '12%', top: '42%', left: '48%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, #C24824 0%, #8A2A14 60%, transparent 95%)', filter: 'blur(0.5px)' }} />
        </>
      )}
      {d.type === 'moon' && (
        <>
          {/* maria — dark patches */}
          <div style={{ position: 'absolute', width: '30%', height: '26%', top: '22%', left: '22%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(90,80,130,0.55) 0%, rgba(60,55,95,0.3) 60%, transparent 90%)', filter: 'blur(0.6px)' }} />
          <div style={{ position: 'absolute', width: '20%', height: '18%', top: '56%', left: '45%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(90,80,130,0.5) 0%, transparent 80%)', filter: 'blur(0.5px)' }} />
          {/* craters */}
          <div style={{ position: 'absolute', width: '10%', height: '10%', top: '42%', left: '62%', borderRadius: '50%',
            background: 'rgba(60,55,90,0.45)', boxShadow: 'inset -1px -1px 2px rgba(255,255,255,0.25)' }} />
          <div style={{ position: 'absolute', width: '7%', height: '7%', top: '65%', left: '28%', borderRadius: '50%',
            background: 'rgba(60,55,90,0.4)', boxShadow: 'inset -1px -1px 1.5px rgba(255,255,255,0.22)' }} />
        </>
      )}
      {d.type === 'mercury' && (
        <>
          {/* craters all over */}
          {[[20,30,8],[55,22,6],[35,60,7],[70,55,9],[50,75,6],[25,50,5]].map(([x,y,s], i) => (
            <div key={i} style={{ position: 'absolute', width: `${s}%`, height: `${s}%`,
              top: `${y}%`, left: `${x}%`, borderRadius: '50%',
              background: 'rgba(30,50,42,0.5)',
              boxShadow: 'inset -1px -1px 1.5px rgba(255,255,255,0.2)' }} />
          ))}
        </>
      )}
      {d.type === 'saturn' && (
        <>
          {/* subtle horizontal bands */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'repeating-linear-gradient(180deg, transparent 0, transparent 10%, rgba(120,80,30,0.18) 10%, rgba(120,80,30,0.18) 13%, transparent 13%, transparent 23%)' }} />
        </>
      )}
      {d.type === 'mars' && (
        <>
          {/* polar ice cap */}
          <div style={{ position: 'absolute', width: '45%', height: '22%', top: '4%', left: '27%', borderRadius: '50%',
            background: 'radial-gradient(ellipse, rgba(255,230,220,0.8) 0%, rgba(255,200,180,0.4) 50%, transparent 85%)', filter: 'blur(0.8px)' }} />
          {/* canyons / craters */}
          <div style={{ position: 'absolute', width: '30%', height: '10%', top: '55%', left: '30%', borderRadius: '50%',
            background: 'rgba(100,30,15,0.55)', filter: 'blur(0.5px)' }} />
          <div style={{ position: 'absolute', width: '14%', height: '14%', top: '38%', left: '60%', borderRadius: '50%',
            background: 'rgba(100,30,15,0.45)' }} />
        </>
      )}
      {d.type === 'ketu' && (
        <>
          {/* Ketu — shadow node, depicted as a smoky lavender orb with nebulous swirls */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(ellipse at 35% 30%, rgba(180,150,220,0.45) 0%, rgba(100,75,150,0.22) 45%, transparent 78%)',
            filter: 'blur(1px)' }} />
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'radial-gradient(ellipse at 65% 70%, rgba(70,50,110,0.55) 0%, transparent 45%)',
            filter: 'blur(1.2px)' }} />
          {/* swirling mist bands */}
          <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
            background: 'repeating-radial-gradient(ellipse at 50% 50%, transparent 0%, rgba(200,180,240,0.1) 12%, transparent 22%)',
            opacity: 0.6, filter: 'blur(0.6px)' }} />
        </>
      )}

      {/* Specular highlight — every planet */}
      <div style={{ position: 'absolute', width: '40%', height: '38%', top: '10%', left: '15%', borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, rgba(255,255,255,0.12) 40%, transparent 75%)',
        filter: 'blur(0.5px)', pointerEvents: 'none' }} />
      {/* Terminator shadow */}
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
        background: 'radial-gradient(circle at 78% 78%, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.15) 38%, transparent 62%)',
        pointerEvents: 'none' }} />
      {/* Rim light */}
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%',
        boxShadow: `inset 0 0 ${size * 0.12}px ${d.grad[0]}40`, pointerEvents: 'none' }} />
    </div>
  );

  // Saturn gets iconic rings wrapping around the sphere — rings absolute positioned,
  // sphere stays exactly at origin so planet anchors correctly on its orbit.
  if (d.type === 'saturn') {
    const ringW = size * 2.1;
    const ringH = size * 0.38;
    return (
      <div style={{ width: size, height: size, position: 'relative', overflow: 'visible' }}>
        {/* Back half of ring (behind planet) */}
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          width: ringW, height: ringH,
          marginLeft: -ringW / 2, marginTop: -ringH / 2,
          borderRadius: '50%',
          background: `radial-gradient(ellipse, transparent 30%, ${d.grad[1]}aa 38%, ${d.grad[2]}cc 50%, ${d.grad[1]}88 62%, transparent 72%)`,
          transform: 'rotate(-18deg)',
          clipPath: 'polygon(0 0, 100% 0, 100% 50%, 0 50%)',
          filter: 'blur(0.4px)',
        }} />
        {/* Planet sphere — centered at origin */}
        <div style={{ position: 'absolute', inset: 0 }}>
          {sphere}
        </div>
        {/* Front half of ring */}
        <div style={{
          position: 'absolute', left: '50%', top: '50%',
          width: ringW, height: ringH,
          marginLeft: -ringW / 2, marginTop: -ringH / 2,
          borderRadius: '50%',
          background: `radial-gradient(ellipse, transparent 30%, ${d.grad[0]}cc 38%, ${d.grad[1]}ee 50%, ${d.grad[0]}aa 62%, transparent 72%)`,
          transform: 'rotate(-18deg)',
          clipPath: 'polygon(0 50%, 100% 50%, 100% 100%, 0 100%)',
          filter: 'blur(0.4px)',
          boxShadow: `0 0 ${size * 0.3}px ${d.color}50`,
        }} />
      </div>
    );
  }

  return sphere;
};

const LoadingPastStoryReels = ({ embedded = false, storyProgress = 0 }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const source = searchParams.get('source');
  const abortRef = useRef(false);
  const [phraseIdx, setPhraseIdx] = useState(0);

  // Detect native app (Capacitor iOS/Android) vs web (/watch landing flow)
  const isNativeApp = !!(typeof window !== 'undefined' && window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function' && window.Capacitor.isNativePlatform());
  // Robust /watch detection: URL pathname is the AUTHORITATIVE source.
  // Regardless of whether `source` query param or `landing_flow` localStorage flag was
  // cleared (e.g., after first download click), if the user is on /watch or /watch1
  // we ALWAYS show Download App CTAs — never Login.
  const onWatchPath = typeof window !== 'undefined' &&
    (window.location.pathname === '/watch' || window.location.pathname === '/watch1' ||
     window.location.pathname.startsWith('/watch/') || window.location.pathname.startsWith('/watch1/'));
  // Strong landing-flow signals (URL-based) — these override any stale localStorage flags
  // like demo_source='emergent' that may have leaked from a prior /watch1 visit.
  const hasStrongLandingSignal = onWatchPath || source === 'landing' || source === 'watch';
  // Emergent demo only applies if NO strong landing signal AND the demo flag is set.
  const isEmergentDemo = typeof window !== 'undefined' &&
    !hasStrongLandingSignal &&
    localStorage.getItem('demo_source') === 'emergent';
  // Landing flow flag is set by /watch → handleCTA. Only visitors who came through
  // /watch get Download App CTAs. Direct URL access on web OR native app → normal Login/Skip flow.
  const isLandingFlow = typeof window !== 'undefined' &&
    !isNativeApp && !isEmergentDemo &&
    (hasStrongLandingSignal || localStorage.getItem('landing_flow') === 'true');
  // Source tag for downstream /your-story routing:
  //   landing flow (came via /watch) → "landing"  (Download App CTAs, never /login)
  //   everything else                → "emergent" (Skip/Continue → /login flow)
  const storySrc = isLandingFlow ? 'landing' : 'emergent';

  // IMPORTANT: past_story_viewed / free_story_used are NEVER auto-set on mount here.
  // They are set ONLY when the user explicitly presses:
  //   - Login button (native)          → /login
  //   - View All Domains button        → /your-story?source=...
  //   - Download App Store / Play Store (web landing flow) → external navigation
  // This way, if the user closes the app / tab while planets are still loading or
  // before pressing any forward button, they resume on the SAME planets screen.

  useEffect(() => {
    const t = setInterval(() => setPhraseIdx(i => (i + 1) % PHRASES.length), 3500);
    return () => clearInterval(t);
  }, []);

  // ---- State (unchanged) ----
  const getUserHash = () => { try { const bd = localStorage.getItem('birthDetails') || ''; let h = 0; for (let i = 0; i < bd.length; i++) { h = ((h << 5) - h) + bd.charCodeAt(i); h |= 0; } return String(h); } catch (_) { return ''; } };
  const userHash = getUserHash();
  const saved = useRef(null);
  try { const r = JSON.parse(localStorage.getItem(STATE_KEY)); if (r?.userHash === userHash) saved.current = r; else localStorage.removeItem(STATE_KEY); } catch (_) {}

  const [completed, setCompleted] = useState(saved.current?.completed || []);
  const completedRef = useRef(saved.current?.completed || []);
  const [loadingKey, setLoadingKey] = useState(null);
  const [domPct, setDomPct] = useState(saved.current?.domPct || {});
  const [ready, setReady] = useState(saved.current?.ready || saved.current?.completed?.length || 0);
  const [allDone, setAllDone] = useState(saved.current?.allDone || false);
  const genRef = useRef(false);

  useEffect(() => { localStorage.setItem(STATE_KEY, JSON.stringify({ completed, domPct, ready, allDone, userHash })); }, [completed, domPct, ready, allDone, userHash]);

  const [sz, setSz] = useState({ w: window.innerWidth, h: window.innerHeight });
  useEffect(() => { const fn = () => setSz({ w: window.innerWidth, h: window.innerHeight }); window.addEventListener('resize', fn); return () => window.removeEventListener('resize', fn); }, []);

  // ---- Responsive size buckets ----
  const tiny = sz.w < 360;
  const mob  = sz.w < 500;
  const tab  = sz.w >= 500 && sz.w < 900;
  const desk = sz.w >= 900;

  const sizes = tiny  ? { planet: 30, sage: 60,  preferOuter: 140 }
              : mob   ? { planet: 38, sage: 74,  preferOuter: 175 }
              : tab   ? { planet: 56, sage: 98,  preferOuter: 290 }
                      : { planet: 86, sage: 140, preferOuter: 720 };

  const planetSz = sizes.planet;
  const sageSz   = sizes.sage;
  const tilt     = 50;
  const cosT     = 0.6428; // cos(50°)

  // Max orbit radius that fits the viewport after accounting for planet edge & tilt
  const maxFromW = sz.w / 2 - planetSz / 2 - 12;
  const maxFromH = (sz.h - 200) / (2 * cosT) - planetSz / 2;
  const outerR = Math.max(sizes.preferOuter * 0.6, Math.min(sizes.preferOuter, maxFromW, maxFromH));

  // Innermost orbit must clear the Sage (+ planet radius + glow + label pill breathing room)
  // Desktop needs a bigger buffer because label pill sits below the planet toward the sage.
  const clearBuf = desk ? 140 : 50;
  const innerR = Math.max(outerR * 0.30, sageSz / 2 + planetSz / 2 + clearBuf);
  const stepR  = (outerR - innerR) / 7;
  const systemSz = outerR * 2 + planetSz + 80;

  // ---- Audio gen (unchanged) ----
  const generateAudio = useCallback(async () => {
    if (genRef.current || allDone) return; genRef.current = true;
    let sd = localStorage.getItem('unified_story_data');
    if (!sd) { try { const did = localStorage.getItem('device_id') || localStorage.getItem('taraanaya_device_id'); const uid = localStorage.getItem('user_id'); const lid = uid || did; if (lid) { const r = await fetch(`${API_URL}/api/unified-story/load/${lid}${did ? '?device_id=' + did : ''}`); if (r.ok) { const d = await r.json(); if (d?.story_data) sd = JSON.stringify(d.story_data); } } } catch (e) {} }
    if (!sd) { navigate(source ? `/your-story?source=${source}` : '/your-story', { replace: true }); return; }
    let ud; try { ud = JSON.parse(sd); } catch (_) { navigate('/your-story', { replace: true }); return; }
    const { buildStoryData, splitTextIntoChunks, MAX_CHUNK_CHARS } = await import('../utils/storyTextProcessor');
    const langs = ['hinglish', 'hindi', 'english'], domOrder = DOMAINS.map(d => d.key), dc = {};
    let ri = 0;
    for (const lang of langs) { const { sections } = buildStoryData(ud, lang); for (const sec of sections) { const dom = (sec.domain || '').toLowerCase().trim(); let m = null; for (const k of domOrder) { if (dom === k || dom.startsWith(k + '_') || dom.startsWith(k + ' ')) { m = k; break; } } if (!m && dom.includes('spiritual')) m = 'spiritual'; if (!m && dom) { for (const k of domOrder) { if (k.length > 3 && dom.includes(k)) { m = k; break; } } } if (!m) { m = domOrder[ri % domOrder.length]; ri++; } if (!dc[m]) dc[m] = {}; if (!dc[m][lang]) dc[m][lang] = []; dc[m][lang].push(...splitTextIntoChunks(sec.text, MAX_CHUNK_CHARS)); } }
    let r = completed.length;
    for (const dk of domOrder) { if (abortRef.current) break; if (completedRef.current.includes(dk)) { r++; continue; } setLoadingKey(dk); setDomPct(p => ({ ...p, [dk]: 0 })); const ch = dc[dk] || {}; let tot = 0, dn = 0; for (const l of langs) tot += (ch[l] || []).length; if (tot === 0) { setDomPct(p => ({ ...p, [dk]: 100 })); setCompleted(p => { const n = [...new Set([...p, dk])]; completedRef.current = n; return n; }); r++; setReady(r); setLoadingKey(null); continue; }
      const lp = langs.map(async (lang) => { const lc = ch[lang] || []; if (!lc.length) return; for (let b = 0; b < lc.length; b += 5) { const batch = lc.slice(b, b + 5); await Promise.all(batch.map(text => fetch(`${API_URL}/api/tts/edge`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ text: text.substring(0, 600), language: lang }) }).then(async res => { if (res.ok) { const d = await res.json(); if (d.success && d.audio) { try { const bc = atob(d.audio); const ba = new Uint8Array(bc.length); for (let k = 0; k < bc.length; k++) ba[k] = bc.charCodeAt(k); saveToGlobalStore(lang, text, URL.createObjectURL(new Blob([ba], { type: 'audio/mpeg' })), `data:audio/mpeg;base64,${d.audio}`); } catch (_) {} } } }).catch(() => {}))); dn += batch.length; setDomPct(p => ({ ...p, [dk]: Math.min(Math.round((dn / Math.max(tot, 1)) * 100), 100) })); } });
      await Promise.all(lp); setDomPct(p => ({ ...p, [dk]: 100 })); setCompleted(p => { const n = [...new Set([...p, dk])]; completedRef.current = n; return n; }); r++; setReady(r); setLoadingKey(null); }
    setAllDone(true); localStorage.setItem('audio_generation_complete', 'true');
  }, [allDone, completed, navigate, source]);

  useEffect(() => { abortRef.current = false; if (!allDone) { if (embedded) { const iv = setInterval(() => { if (localStorage.getItem('unified_story_data')) { clearInterval(iv); generateAudio(); } }, 500); return () => { abortRef.current = true; clearInterval(iv); }; } else { generateAudio(); } } return () => { abortRef.current = true; }; }, [allDone, generateAudio, embedded]);

  return (
    <div data-testid="past-reels-loading" className="fixed inset-0 overflow-hidden" style={{ background: '#030308' }}>

      {/* Deep space gradient */}
      <div className="absolute inset-0" style={{
        background:
          'radial-gradient(ellipse at 50% 38%, rgba(40,20,80,0.35) 0%, transparent 55%),' +
          'radial-gradient(ellipse at 78% 72%, rgba(80,20,60,0.18) 0%, transparent 45%),' +
          'radial-gradient(ellipse at 18% 80%, rgba(20,40,90,0.22) 0%, transparent 50%),' +
          '#030308',
      }} />

      {/* Stars — layered parallax */}
      <div className="absolute inset-0 pointer-events-none">
        {Array.from({ length: 90 }).map((_, i) => {
          const big = i % 11 === 0;
          const gold = i % 13 === 0;
          return (
            <div key={i} className="absolute rounded-full" style={{
              left: `${(i * 17.3 + 2) % 100}%`, top: `${(i * 23.7 + 4) % 100}%`,
              width: big ? 2.5 : 1 + (i % 3) * 0.35,
              height: big ? 2.5 : 1 + (i % 3) * 0.35,
              background: gold ? '#F3E5AB' : i % 5 === 0 ? '#a8c8ff' : '#fff',
              boxShadow: big ? `0 0 4px ${gold ? '#F3E5AB' : '#ffffff'}` : 'none',
              animation: `twk ${2 + (i % 5) * 0.7}s ease-in-out infinite`,
              animationDelay: `${(i * 0.3) % 5}s`,
            }} />
          );
        })}
      </div>

      {/* Back button removed — planets dashboard IS home; user shouldn't leave here before picking a domain */}

      {/* Title */}
      <div className="relative z-10 text-center" style={{ paddingTop: mob ? 44 : desk ? 64 : 52 }}>
        <h1 style={{
          fontFamily: "'Cormorant Garamond',serif",
          fontSize: mob ? 17 : desk ? 28 : 21, fontWeight: 300, color: '#F3E5AB',
          letterSpacing: '0.35em', textTransform: 'uppercase', marginBottom: desk ? 6 : 3,
        }}>Past Story Reels</h1>
        <p style={{
          color: allDone ? 'rgba(212,175,55,0.75)' : 'rgba(170,170,190,0.7)',
          fontSize: mob ? 10 : desk ? 14 : 12, letterSpacing: '0.12em', textTransform: 'uppercase',
        }}>
          {allDone ? 'Tap a planet to listen' : ready > 0 ? `Preparing ${Math.min(ready, 8)} of 8` : embedded && storyProgress < 100 ? `Building story... ${storyProgress}%` : 'Awakening your story...'}
        </p>
        {!allDone && (
          <div style={{ maxWidth: desk ? 220 : 160, margin: '9px auto 0', height: desk ? 2 : 1.5, borderRadius: 1, background: 'rgba(212,175,55,0.08)', overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${Math.min((ready / 8) * 100, 100)}%`, background: 'linear-gradient(90deg,#D4AF37,#E8C170)', borderRadius: 1, transition: 'width 0.6s ease' }} />
          </div>
        )}
      </div>

      {/* ================ 3D SOLAR SYSTEM ================ */}
      <div className="absolute left-1/2" style={{
        top: '50%', transform: 'translate(-50%, -50%)',
        width: systemSz, height: systemSz,
        perspective: '1400px', perspectiveOrigin: '50% 50%',
        zIndex: 10,
      }}>
        <div style={{
          position: 'absolute', inset: 0,
          transformStyle: 'preserve-3d',
          transform: `rotateX(${tilt}deg)`,
        }}>
          {/* Render outer orbits first (bottom of stacking), innermost planets LAST so they sit
              on top of any overlapping larger hit-areas → HEALTH (innermost) is always clickable. */}
          {DOMAINS.slice().reverse().map((d) => {
            const i = DOMAINS.indexOf(d);
            const radius   = innerR + stepR * i;
            const duration = 28 / d.speed;              // seconds per full orbit
            const delay    = -(d.startDeg / 360) * duration;
            const isDone   = completed.includes(d.key);
            const isLoading= loadingKey === d.key;
            const pct      = domPct[d.key] || 0;
            const active   = isDone || isLoading;

            return (
              <React.Fragment key={d.id}>
                {/* Orbit ring — flat in rotated plane, appears elliptical.
                    pointer-events: none so planet buttons underneath always receive clicks. */}
                <div style={{
                  position: 'absolute', left: '50%', top: '50%',
                  width: radius * 2, height: radius * 2,
                  marginLeft: -radius, marginTop: -radius,
                  borderRadius: '50%',
                  border: `1px solid ${active ? d.color + '55' : 'rgba(212,175,55,0.11)'}`,
                  boxShadow: active
                    ? `0 0 28px ${d.color}22, inset 0 0 36px ${d.color}12`
                    : 'inset 0 0 22px rgba(212,175,55,0.04)',
                  transition: 'border-color 0.8s ease, box-shadow 0.8s ease',
                  pointerEvents: 'none',
                }} />

                {/* Rotator — paused at startDeg so planets are always stable and tappable.
                    Orbital motion removed for reliability — magic is in the static 3D tilt + characters. */}
                <div style={{
                  position: 'absolute', left: '50%', top: '50%',
                  width: radius * 2, height: radius * 2,
                  marginLeft: -radius, marginTop: -radius,
                  transform: `rotate(${d.startDeg}deg)`,
                  transformStyle: 'preserve-3d',
                }}>
                  {/* Wide planet-holder at 12 o'clock — center coincides with orbit anchor.
                      Wide + tall so the label pill lives in the same tree as the sphere → perfect sync. */}
                  <div style={{
                    position: 'absolute', left: '50%', top: 0,
                    width: Math.max(240, planetSz * 4), height: planetSz + 70,
                    marginLeft: -Math.max(240, planetSz * 4) / 2, marginTop: -(planetSz + 70) / 2,
                    // Static counter-rotation so content faces the camera upright
                    transform: `rotate(${-d.startDeg}deg)`,
                    transformStyle: 'preserve-3d',
                    overflow: 'visible',
                  }}>
                    <div style={{
                      transform: `rotateX(${-tilt}deg)`,
                      width: '100%', height: '100%',
                      position: 'relative',
                      overflow: 'visible',
                      transformStyle: 'preserve-3d',
                    }}>
                      {/* Planet sphere — centered on holder center (= orbit anchor).
                          Button hit-area is ~1.8× the sphere → easy tap target even on small screens. */}
                      <button
                        data-testid={`planet-${d.key}`}
                        onClick={() => { if (isDone) { localStorage.setItem('watching_story', 'true'); navigate(`/your-story?domain=${d.key}&mode=reel&source=${storySrc}`); } }}
                        onPointerDown={(e) => { if (isDone) { e.stopPropagation(); localStorage.setItem('watching_story', 'true'); navigate(`/your-story?domain=${d.key}&mode=reel&source=${storySrc}`); } }}
                        disabled={!isDone}
                        style={{
                          position: 'absolute',
                          left: '50%', top: (planetSz + 70) / 2,
                          transform: 'translate(-50%, -50%) translateZ(1px)',
                          width: planetSz * 1.5, height: planetSz * 1.5, border: 'none',
                          background: 'transparent', padding: 0,
                          cursor: isDone ? 'pointer' : 'default',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          borderRadius: '50%',
                          touchAction: 'manipulation',
                          WebkitTapHighlightColor: 'transparent',
                          isolation: 'isolate',
                        }}
                      >
                        {/* Inner wrapper holds the visible sphere + glow + play icon */}
                        <div style={{ width: planetSz, height: planetSz, position: 'relative', pointerEvents: 'none' }}>
                        {/* Outer glow */}
                        {active && (
                          <div style={{
                            position: 'absolute', inset: -planetSz * 0.4,
                            borderRadius: '50%',
                            background: `radial-gradient(circle, ${d.color}55 0%, ${d.color}18 35%, transparent 70%)`,
                            filter: 'blur(6px)',
                            opacity: isDone ? 1 : 0.6,
                            pointerEvents: 'none',
                          }} />
                        )}

                        {/* Loading progress ring */}
                        {isLoading && (
                          <svg style={{ position: 'absolute', inset: -4, width: planetSz + 8, height: planetSz + 8, transform: 'rotate(-90deg)', pointerEvents: 'none' }}>
                            <circle cx={(planetSz + 8) / 2} cy={(planetSz + 8) / 2} r={planetSz / 2 + 1} fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="1.5" />
                            <circle cx={(planetSz + 8) / 2} cy={(planetSz + 8) / 2} r={planetSz / 2 + 1} fill="none" stroke={d.color} strokeWidth="2"
                              strokeDasharray={`${Math.PI * (planetSz + 2)}`}
                              strokeDashoffset={`${Math.PI * (planetSz + 2) * (1 - pct / 100)}`}
                              strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
                          </svg>
                        )}

                        {/* Planet sphere — characterful by type */}
                        <div style={{
                          width: planetSz, height: planetSz, position: 'relative',
                          opacity: isDone ? 1 : isLoading ? 0.85 : 0.32,
                          filter: (!isDone && !isLoading) ? 'grayscale(0.55) brightness(0.55)' : 'none',
                          transition: 'opacity 0.8s ease, filter 0.8s ease',
                          borderRadius: '50%',
                          boxShadow: isDone
                            ? `0 0 ${planetSz * 0.55}px ${d.color}60, 0 0 ${planetSz * 0.18}px ${d.color}90`
                            : 'none',
                        }}>
                          <PlanetBody d={d} size={planetSz} active={isDone || isLoading} />
                        </div>

                        {/* Play icon */}
                        {isDone && (
                          <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, pointerEvents: 'none' }}>
                            <div style={{
                              width: planetSz * 0.5, height: planetSz * 0.5, borderRadius: '50%',
                              background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)',
                              display: 'flex', alignItems: 'center', justifyContent: 'center',
                              boxShadow: `0 0 8px ${d.color}60`,
                            }}>
                              <Play style={{ width: planetSz * 0.22, height: planetSz * 0.22, color: '#fff', marginLeft: 1.5 }} fill="currentColor" strokeWidth={0} />
                            </div>
                          </div>
                        )}

                        {/* Loading pulse ring */}
                        {isLoading && (
                          <div style={{
                            position: 'absolute', inset: -1, borderRadius: '50%',
                            border: `1.5px solid ${d.color}50`,
                            animation: 'lpulse 2s ease-in-out infinite',
                            pointerEvents: 'none',
                          }} />
                        )}
                        </div>
                      </button>

                      {/* Label pill — in the SAME counter-rotating chain as the sphere → perfectly synced */}
                      <p style={{
                        position: 'absolute',
                        left: '50%',
                        top: (planetSz + 70) / 2 + planetSz / 2 + 8,
                        transform: 'translateX(-50%) translateZ(20px)',
                        margin: 0,
                        padding: isDone ? (desk ? '5px 14px' : '3px 10px') : (desk ? '4px 13px' : '2px 9px'),
                        borderRadius: desk ? 14 : 12,
                        background: isDone
                          ? `linear-gradient(135deg, ${d.color}28 0%, rgba(8,8,20,0.94) 72%)`
                          : 'rgba(8,8,20,0.78)',
                        border: isDone ? `1px solid ${d.color}65` : '1px solid rgba(255,255,255,0.07)',
                        backdropFilter: 'blur(5px)',
                        WebkitBackdropFilter: 'blur(5px)',
                        color: isDone ? '#F3E5AB' : isLoading ? 'rgba(243,229,171,0.6)' : 'rgba(170,170,190,0.65)',
                        fontSize: mob ? 9 : desk ? 13 : 11, textAlign: 'center',
                        fontWeight: isDone ? 700 : 500, letterSpacing: '.1em', textTransform: 'uppercase',
                        whiteSpace: 'nowrap', transition: 'color 0.6s ease, background 0.6s ease',
                        textShadow: isDone ? `0 0 10px ${d.color}60` : 'none',
                        boxShadow: isDone ? `0 0 16px ${d.color}35, 0 3px 8px rgba(0,0,0,0.6)` : '0 2px 5px rgba(0,0,0,0.45)',
                        pointerEvents: 'none',
                      }}>
                        <span>{d.name}</span>
                      </p>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })}

          {/* Sage center — counter-rotated to face camera.
              pointer-events: none so clicks pass through to the HEALTH planet button beneath. */}
          <div style={{
            position: 'absolute', left: '50%', top: '50%',
            transform: `translate(-50%, -50%) rotateX(${-tilt}deg)`,
            zIndex: 3,
            pointerEvents: 'none',
          }}>
            <div style={{
              position: 'absolute', inset: -24, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)',
              animation: 'sagePulse 4s ease-in-out infinite',
              filter: 'blur(2px)',
            }} />
            <div style={{
              position: 'absolute', inset: -10, borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,220,120,0.35) 0%, transparent 60%)',
              animation: 'sagePulse 2.5s ease-in-out infinite',
            }} />
            {embedded && storyProgress < 100 && (
              <svg style={{ position: 'absolute', inset: -10, width: sageSz + 20, height: sageSz + 20, transform: 'rotate(-90deg)' }}>
                <circle cx={(sageSz + 20) / 2} cy={(sageSz + 20) / 2} r={(sageSz + 12) / 2} fill="none" stroke="rgba(212,175,55,0.08)" strokeWidth="2" />
                <circle cx={(sageSz + 20) / 2} cy={(sageSz + 20) / 2} r={(sageSz + 12) / 2} fill="none" stroke="#D4AF37" strokeWidth="2.5"
                  strokeDasharray={`${Math.PI * (sageSz + 12)}`} strokeDashoffset={`${Math.PI * (sageSz + 12) * (1 - storyProgress / 100)}`}
                  strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.5s ease' }} />
              </svg>
            )}
            <motion.div animate={{ y: [0, -3, 0] }} transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}>
              <img src="/sage-final.png" alt="Sage" style={{ width: sageSz, height: sageSz, objectFit: 'contain', filter: 'drop-shadow(0 0 22px rgba(212,175,55,0.5))' }} />
            </motion.div>
            {embedded && storyProgress > 0 && storyProgress < 100 && (
              <p style={{ textAlign: 'center', color: '#D4AF37', fontSize: 10, fontWeight: 600, marginTop: 1, fontFamily: 'monospace' }}>{storyProgress}%</p>
            )}
          </div>
        </div>
      </div>

      {/* ===== 2D overlay click layer — invisible buttons at each planet's static screen position.
           Renders at ALL times (loading + allDone) so clicks ALWAYS work on tappable (isDone) planets. ===== */}
      <div style={{
        position: 'absolute', left: '50%', top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 0, height: 0,
        zIndex: 40,
      }}>
        {DOMAINS.map((d, i) => {
          const radius = innerR + stepR * i;
          const θ = d.startDeg * Math.PI / 180;
          const cosT = 0.6428;
          const lx = radius * Math.sin(θ);
          const ly = -radius * Math.cos(θ) * cosT;
          const hit = planetSz * 1.55;
          const tappable = completed.includes(d.key);
          return (
            <button
              key={`hit-${d.id}`}
              data-testid={`planet-hit-${d.key}`}
              aria-label={`Open ${d.name} story`}
              disabled={!tappable}
              onClick={() => { if (tappable) { localStorage.setItem('watching_story', 'true'); navigate(`/your-story?domain=${d.key}&mode=reel&source=${storySrc}`); } }}
              style={{
                position: 'absolute',
                left: lx, top: ly,
                width: hit, height: hit,
                marginLeft: -hit / 2, marginTop: -hit / 2,
                border: 'none', background: 'transparent', padding: 0,
                cursor: tappable ? 'pointer' : 'default',
                borderRadius: '50%',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                pointerEvents: tappable ? 'auto' : 'none',
              }}
            />
          );
        })}
      </div>

      {/* Bottom controls */}
      <div style={{
        position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 20,
        padding: desk ? '0 40px' : '0 20px',
        paddingBottom: desk ? 32 : 'max(26px, env(safe-area-inset-bottom, 26px))',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: desk ? 14 : 10,
        background: desk ? 'linear-gradient(180deg, transparent 0%, rgba(3,3,8,0.85) 55%, rgba(3,3,8,0.96) 100%)' : 'none',
        paddingTop: desk ? 70 : 0,
      }}>
        {!allDone && (
          <AnimatePresence mode="wait">
            <motion.p key={phraseIdx} initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.5 }}
              style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: mob ? 13 : desk ? 18 : 15, fontStyle: 'italic', color: 'rgba(243,229,171,0.6)', textAlign: 'center', maxWidth: desk ? 360 : 260, letterSpacing: desk ? '0.02em' : 0 }}>
              {PHRASES[phraseIdx]}
            </motion.p>
          </AnimatePresence>
        )}
        <div style={{ display: 'flex', gap: desk ? 14 : mob ? 6 : 10, justifyContent: 'center', width: '100%', maxWidth: desk ? 520 : 380, padding: '0 8px' }}>
          <AnimatePresence>
            {allDone && (
              <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                whileTap={{ scale: 0.96 }} data-testid="explore-all-btn"
                onClick={() => { localStorage.setItem('watching_story', 'true'); localStorage.setItem('past_story_viewed', 'true'); localStorage.setItem('free_story_used', 'true'); /* PRESERVE landing_flow — user is still in /watch journey, may navigate back to planets. Removing this caused Login button to appear on back-nav. Only cleared on actual app install/download. */ try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' }); } catch (e) {} pushStoryStateToServer({ past_story_viewed: true, free_story_used: true /* landing_flow intentionally NOT cleared */ }); navigate(`/your-story?source=${storySrc}`); }}
                style={{
                  padding: desk ? '14px 28px' : mob ? '10px 12px' : '13px 22px',
                  borderRadius: 26, fontWeight: 700,
                  fontSize: mob ? 11 : desk ? 15 : 14,
                  cursor: 'pointer',
                  background: 'linear-gradient(135deg, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0.12) 100%)',
                  border: '1.5px solid rgba(212,175,55,0.65)', color: '#FFE8A8',
                  letterSpacing: '0.06em', flex: 1, maxWidth: desk ? 220 : mob ? 140 : 180,
                  whiteSpace: 'nowrap',
                  boxShadow: '0 0 18px rgba(212,175,55,0.35), 0 4px 10px rgba(0,0,0,0.5)',
                  textShadow: '0 0 8px rgba(212,175,55,0.6)',
                }}>View All Domains</motion.button>
            )}
          </AnimatePresence>
          <AnimatePresence>
            {/* Login / Download CTA appears as soon as the FIRST (Health) domain is ready
                so users who just want to log in / grab the app don't have to wait for all 8. */}
            {completed.length >= 1 && (() => {
              if (!isLandingFlow) {
                return (
                  <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                    whileTap={{ scale: 0.96 }} whileHover={{ y: -1 }}
                    data-testid="login-from-planets-btn"
                    onClick={() => { localStorage.setItem('past_story_viewed', 'true'); localStorage.setItem('free_story_used', 'true'); localStorage.removeItem('watching_story'); try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' }); } catch (e) {} pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false }); navigate('/login', { replace: true }); }}
                    style={{
                      padding: desk ? '14px 30px' : '13px 24px',
                      borderRadius: 28, fontWeight: 700,
                      fontSize: mob ? 13 : desk ? 15 : 14,
                      cursor: 'pointer',
                      background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 50%, #B8860B 100%)',
                      border: '1px solid rgba(255, 235, 170, 0.6)',
                      color: '#1a0f00',
                      letterSpacing: '0.1em', textTransform: 'uppercase',
                      flex: 1, maxWidth: desk ? 220 : 180,
                      whiteSpace: 'nowrap',
                      boxShadow: '0 0 22px rgba(212,175,55,0.5), 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
                      textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                    }}>Login</motion.button>
                );
              }
              // Web-First launch: landing flow now also shows the Login (Sign Up) CTA
              // instead of App/Play store download badges.
              const markViewedAndLogin = () => {
                localStorage.setItem('past_story_viewed', 'true');
                localStorage.setItem('free_story_used', 'true');
                localStorage.removeItem('watching_story');
                localStorage.removeItem('landing_flow');
                try { window.Capacitor?.Plugins?.Preferences?.set({ key: 'past_story_viewed', value: 'true' }); } catch (e) {}
                pushStoryStateToServer({ past_story_viewed: true, free_story_used: true, landing_flow: false });
                navigate('/login', { replace: true });
              };
              return (
                <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                  whileTap={{ scale: 0.96 }} whileHover={{ y: -1 }}
                  data-testid="signup-from-planets-btn"
                  onClick={markViewedAndLogin}
                  style={{
                    padding: desk ? '14px 30px' : '13px 24px',
                    borderRadius: 28, fontWeight: 700,
                    fontSize: mob ? 13 : desk ? 15 : 14,
                    cursor: 'pointer',
                    background: 'linear-gradient(135deg, #F3E5AB 0%, #D4AF37 50%, #B8860B 100%)',
                    border: '1px solid rgba(255, 235, 170, 0.6)',
                    color: '#1a0f00',
                    letterSpacing: '0.1em', textTransform: 'uppercase',
                    flex: 1, maxWidth: desk ? 240 : 200,
                    whiteSpace: 'nowrap',
                    boxShadow: '0 0 22px rgba(212,175,55,0.5), 0 4px 14px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.4)',
                    textShadow: '0 1px 0 rgba(255,255,255,0.3)',
                  }}>Sign Up</motion.button>
              );
            })()}
          </AnimatePresence>
        </div>
      </div>

      <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,400&display=swap" rel="stylesheet" />
      <style>{`
        @keyframes twk { 0%,100% { opacity: 0.08; } 50% { opacity: 0.7; } }
        @keyframes lpulse { 0%,100% { transform: scale(1); opacity: 0.4; } 50% { transform: scale(1.35); opacity: 0; } }
        @keyframes sagePulse { 0%,100% { opacity: 0.55; transform: scale(1); } 50% { opacity: 1; transform: scale(1.08); } }
        @keyframes orbitSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes orbitSpinReverse { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      `}</style>
    </div>
  );
};

export default LoadingPastStoryReels;
